import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { auditLogService } from '../../services/auditLogService';
import { AuditLog } from '../../types/admin';
import AuditLogTable from '../../components/admin/AuditLogTable';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

/**
 * AuditLogPage Component
 * Page for viewing and filtering audit log entries
 *
 * Features:
 * - View all audit log entries
 * - Filter by date range, action type, and user
 * - Search by email or user ID
 * - Export to CSV
 * - Sortable table
 * - Pagination
 */
const AuditLogPage: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<number>(7); // Days
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  /**
   * Load logs on mount and when filters change
   */
  useEffect(() => {
    loadLogs();
  }, [dateRange]);

  /**
   * Apply filters when they change
   */
  useEffect(() => {
    applyFilters();
  }, [logs, actionFilter, searchTerm]);

  /**
   * Load audit logs
   */
  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const startDate = startOfDay(subDays(new Date(), dateRange));
      const endDate = endOfDay(new Date());

      const result = await auditLogService.getAuditLogs(
        { startDate, endDate },
        1000 // Get up to 1000 logs
      );
      setLogs(result.logs);
    } catch (err: any) {
      console.error('Error loading logs:', err);
      setError(err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Apply filters to logs
   */
  const applyFilters = () => {
    let filtered = [...logs];

    // Action type filter
    if (actionFilter !== 'all') {
      filtered = filtered.filter((log) => log.action === actionFilter);
    }

    // Search filter (email or user ID)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (log) =>
          log.userEmail.toLowerCase().includes(searchLower) ||
          log.userId.toLowerCase().includes(searchLower) ||
          log.userName?.toLowerCase().includes(searchLower) ||
          log.targetUserEmail?.toLowerCase().includes(searchLower) ||
          log.targetUserId?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredLogs(filtered);
  };

  /**
   * Get unique action types from logs
   */
  const getUniqueActions = (): string[] => {
    const actions = new Set<string>();
    logs.forEach((log) => actions.add(log.action));
    return Array.from(actions).sort();
  };

  /**
   * Export logs to CSV
   */
  const handleExport = () => {
    const csvData = [
      [
        'Timestamp',
        'Action',
        'User Email',
        'User Name',
        'Target Email',
        'IP Address',
        'Details',
      ],
      ...filteredLogs.map((log) => [
        format(log.timestamp, 'yyyy-MM-dd HH:mm:ss'),
        log.action,
        log.userEmail,
        log.userName || '',
        log.targetUserEmail || '',
        log.ipAddress || '',
        log.details ? JSON.stringify(log.details) : '',
      ]),
    ];

    const csvContent = csvData.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-log-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setActionFilter('all');
    setSearchTerm('');
    setDateRange(7);
  };

  /**
   * Get filter count
   */
  const getFilterCount = (): number => {
    let count = 0;
    if (actionFilter !== 'all') count++;
    if (searchTerm) count++;
    return count;
  };

  /**
   * Render loading state
   */
  if (loading && logs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin')}
          sx={{ mb: 3 }}
        >
          Back to Dashboard
        </Button>

        {/* Page Header */}
        <Box mb={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                Audit Log
              </Typography>
              <Typography variant="body1" color="text.secondary">
                View all admin actions and system activities
              </Typography>
            </Box>

            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadLogs}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={loading || filteredLogs.length === 0}
              >
                Export CSV
              </Button>
            </Box>
          </Box>

          {/* Statistics */}
          <Paper sx={{ p: 2, bgcolor: 'primary.light' }}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Chip
                label={`${filteredLogs.length} Entries`}
                color="primary"
                sx={{ fontWeight: 600 }}
              />
              <Divider orientation="vertical" flexItem />
              <Typography variant="body2" color="text.secondary">
                Last {dateRange} days
              </Typography>
              {getFilterCount() > 0 && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Chip
                    label={`${getFilterCount()} filter${getFilterCount() > 1 ? 's' : ''} active`}
                    size="small"
                    color="warning"
                    onDelete={handleClearFilters}
                  />
                </>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Filters
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(Number(e.target.value))}
                  label="Date Range"
                >
                  <MenuItem value={1}>Last 24 Hours</MenuItem>
                  <MenuItem value={7}>Last 7 Days</MenuItem>
                  <MenuItem value={30}>Last 30 Days</MenuItem>
                  <MenuItem value={90}>Last 90 Days</MenuItem>
                  <MenuItem value={365}>Last Year</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action Type</InputLabel>
                <Select
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  label="Action Type"
                >
                  <MenuItem value="all">All Actions</MenuItem>
                  {getUniqueActions().map((action) => (
                    <MenuItem key={action} value={action}>
                      {action.replace(/_/g, ' ').toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search User"
                placeholder="Search by email or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
                disabled={getFilterCount() === 0}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Audit Log Table */}
        <AuditLogTable logs={filteredLogs} loading={loading} />

        {/* Empty State for No Results */}
        {filteredLogs.length === 0 && !loading && logs.length > 0 && (
          <Paper sx={{ p: 6, textAlign: 'center', mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              No Results Found
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Try adjusting your filters to see more results.
            </Typography>
            <Button variant="outlined" onClick={handleClearFilters}>
              Clear All Filters
            </Button>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default AuditLogPage;
